/** The Call Settings tabs, in order. `?tab=` holds the id. */
export const CALL_SETTINGS_TABS = [
  { value: 'general', label: 'General' },
  { value: 'numbers', label: 'Numbers' },
  { value: 'flows', label: 'Call flows' },
  { value: 'team', label: 'Team' },
  { value: 'desk-phones', label: 'Desk phones' },
  { value: 'outcomes', label: 'Outcomes' },
  { value: 'iris', label: 'Iris' },
] as const;

export type CallSettingsTab = (typeof CALL_SETTINGS_TABS)[number]['value'];

export const DEFAULT_TAB: CallSettingsTab = 'general';

/**
 * Tab values from before this layout, and where their contents live now. An
 * old bookmark or in-app link lands on the right tab rather than the default.
 */
const RETIRED: Record<string, CallSettingsTab> = {
  automation: 'general',
  hours: 'general',
  agents: 'team',
  extensions: 'team',
  assignment: 'numbers',
  landlines: 'desk-phones',
  receptionist: 'iris',
  ivr: 'flows',
};

export function resolveTab(raw: unknown): CallSettingsTab {
  if (CALL_SETTINGS_TABS.some((t) => t.value === raw)) return raw as CallSettingsTab;
  return (typeof raw === 'string' && RETIRED[raw]) || DEFAULT_TAB;
}

/** Where the retired /team/:section pages live now. */
export const TEAM_PAGE_TAB: Record<string, CallSettingsTab> = {
  access: 'team',
  extensions: 'team',
  greetings: 'general',
  sounds: 'general',
  hours: 'general',
  'desk-phones': 'desk-phones',
  outcomes: 'outcomes',
  iris: 'iris',
};

/**
 * The tabs shown on this deployment. Iris is staging-only for now, so on
 * production (app.notifi.co) its tab is left out and `?tab=iris` falls back to
 * General. The deployment comes from the hostname, because one build is
 * promoted from staging to production unchanged.
 */
export function visibleTabs(deployment: string) {
  return CALL_SETTINGS_TABS.filter((t) => t.value !== 'iris' || deployment !== 'production');
}
